import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  ChevronLeft, ChevronRight, Check, Copy, Share2, ExternalLink,
  Trophy, Users, Gavel, Settings, DollarSign, Minus, Plus, Clock,
  CalendarIcon, Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { localInputToInstant, formatDraftTime } from '@/lib/draftTime';

interface RosterConfig {
  QB: number; RB: number; WR: number; TE: number; FLEX: number;
  K: number; P: number; DST: number; COACH: number; IDP: number;
  BN: number; IR: number;
}

const DEFAULT_ROSTER: RosterConfig = {
  QB: 1, RB: 2, WR: 2, TE: 1, FLEX: 1,
  K: 1, P: 1, DST: 1, COACH: 1, IDP: 2,
  BN: 6, IR: 2,
};

const POSITION_INFO: Record<string, string> = {
  QB: 'Quarterback',
  RB: 'Running Back',
  WR: 'Wide Receiver',
  TE: 'Tight End',
  FLEX: 'RB, WR, or TE',
  K: 'Kicker',
  P: 'Punter',
  DST: 'Defense / Special Teams',
  COACH: 'Head Coach',
  IDP: 'Individual Defensive Player (DB, DL, LB)',
  BN: 'Bench',
  IR: 'Injured Reserve',
};

const POSITION_LIMITS: Record<string, [number, number]> = {
  QB: [1, 2], RB: [1, 4], WR: [1, 4], TE: [1, 3], FLEX: [0, 3],
  K: [0, 2], P: [0, 2], DST: [0, 2], COACH: [0, 2], IDP: [0, 4],
  BN: [2, 10], IR: [0, 4],
};

const STEPS = ['League Basics', 'Roster Setup', 'Draft Settings', 'Buy-In & Review'];

interface NFLCreateLeagueWizardProps {
  onComplete?: (leagueId: string) => void;
  onClose?: () => void;
}

export default function NFLCreateLeagueWizard({ onComplete, onClose }: NFLCreateLeagueWizardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1 state
  const [leagueName, setLeagueName] = useState('');
  const [teamCount, setTeamCount] = useState(12);
  // SAB-505: scoring mode. 'sabotage' (default) = inverted trash scoring + full
  // roster. Conventional (standard/half_ppr/ppr) = real fantasy points, and this
  // phase drops DST/COACH/IDP from the roster (DST slot returns with DST scoring).
  const [scoringType, setScoringType] = useState<'sabotage' | 'standard' | 'half_ppr' | 'ppr'>('sabotage');
  const isConventional = scoringType !== 'sabotage';

  // Step 2 state
  const [roster, setRoster] = useState<RosterConfig>({ ...DEFAULT_ROSTER });

  // Switching scoring mode adjusts the roster: conventional zeroes DST/COACH/IDP
  // (not offered this phase); sabotage restores the full-roster defaults.
  const selectScoringType = (t: 'sabotage' | 'standard' | 'half_ppr' | 'ppr') => {
    setScoringType(t);
    setRoster(prev => t === 'sabotage'
      ? { ...prev, DST: prev.DST || 1, COACH: prev.COACH || 1, IDP: prev.IDP || 2 }
      : { ...prev, DST: 0, COACH: 0, IDP: 0 });
  };

  // Step 3 state
  const [draftType, setDraftType] = useState<'snake' | 'slow'>('snake');
  const [draftTimer, setDraftTimer] = useState(90); // seconds for snake, seconds for slow
  // SAB-534 Chunk 2: overnight pause window (slow drafts only, ET hours)
  const [pauseOvernight, setPauseOvernight] = useState(false);
  const [pauseStartHour, setPauseStartHour] = useState(23);
  const [pauseEndHour, setPauseEndHour] = useState(8);
  const [draftDate, setDraftDate] = useState('');

  // Step 4 state
  const [buyIn, setBuyIn] = useState(0);
  // SAB-425: 'private' (default) hides from /nfl/leagues/browse and requires
  // an invite code to join. 'public' opens it to anyone with open seats.
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');

  // Success state
  const [createdLeague, setCreatedLeague] = useState<{
    id: string;
    invite_code: string;
    invite_link: string;
    name: string;
  } | null>(null);

  const totalSlots = useMemo(() =>
    Object.values(roster).reduce((a, b) => a + b, 0),
    [roster]
  );

  const starterSlots = useMemo(() =>
    totalSlots - roster.BN - roster.IR,
    [totalSlots, roster.BN, roster.IR]
  );

  const canProceed = useMemo(() => {
    switch (step) {
      case 0: return leagueName.trim().length >= 2 && leagueName.trim().length <= 40;
      case 1: return totalSlots >= 10 && totalSlots <= 30;
      case 2: return true;
      case 3: return true;
      default: return false;
    }
  }, [step, leagueName, totalSlots]);

  const updateRoster = (pos: string, delta: number) => {
    setRoster(prev => {
      const current = prev[pos as keyof RosterConfig];
      const [min, max] = POSITION_LIMITS[pos] || [0, 10];
      const next = Math.max(min, Math.min(max, current + delta));
      return { ...prev, [pos]: next };
    });
  };

  const handleCreate = async () => {
    if (!user) return;
    setIsSubmitting(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { toast({ title: 'Not logged in', variant: 'destructive' }); return; }

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-create-league`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            league_name: leagueName.trim(),
            team_count: teamCount,
            draft_type: draftType,
            draft_timer: draftTimer,
            draft_pause_overnight: draftType === 'slow' ? pauseOvernight : false,
            draft_pause_start_hour: pauseStartHour,
            draft_pause_end_hour: pauseEndHour,
            // SAB-788: send an ABSOLUTE INSTANT, not the naive datetime-local string.
            // The raw value has no offset, and the server hands it to a timestamptz column
            // where Postgres (TimeZone=UTC) reads it as UTC — storing 7:15 PM as 19:15Z.
            draft_date: localInputToInstant(draftDate) || undefined,
            buy_in: buyIn,
            visibility,
            roster_config: roster,
            scoring_type: scoringType,
            season_year: 2026,
          }),
        }
      );

      const result = await res.json();
      if (result.error) {
        toast({ title: 'Failed to create league', description: result.error, variant: 'destructive' });
      } else {
        setCreatedLeague({
          id: result.league.id,
          invite_code: result.league.invite_code,
          invite_link: result.invite_link,
          name: result.league.name,
        });
        toast({ title: 'League created!' });
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyInviteLink = async () => {
    if (!createdLeague) return;
    try {
      await navigator.clipboard.writeText(createdLeague.invite_link);
      toast({ title: 'Copied!', description: 'Invite link copied to clipboard' });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const shareInvite = async () => {
    if (!createdLeague) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${createdLeague.name} on Sabotage Fantasy`,
          text: `Join my NFL league: ${createdLeague.name}! Use code ${createdLeague.invite_code}`,
          url: createdLeague.invite_link,
        });
      } catch { /* user cancelled */ }
    } else {
      copyInviteLink();
    }
  };

  // ── Success Screen ──
  if (createdLeague) {
    return (
      <div className="space-y-6 max-w-lg mx-auto">
        <div className="text-center space-y-4 pt-4">
          <div className="h-16 w-16 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-green-400" />
          </div>
          <div>
            <h1 className="text-xl font-display font-bold text-foreground">League Created!</h1>
            <p className="text-sm text-muted-foreground mt-1">{createdLeague.name} is ready</p>
          </div>
        </div>

        {/* Invite Code */}
        <Card className="bg-card border-primary/20">
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Invite Code</p>
            <p className="text-4xl font-mono font-bold text-primary tracking-[0.3em]">
              {createdLeague.invite_code}
            </p>
            <p className="text-xs text-muted-foreground">
              Share this code with your friends to join the league
            </p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" onClick={copyInviteLink} className="bg-primary hover:bg-primary/90">
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy Link
              </Button>
              <Button size="sm" variant="outline" onClick={shareInvite}>
                <Share2 className="h-3.5 w-3.5 mr-1.5" />
                Share
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SAB-591: paid leagues collect the commissioner's own fee from the
            lobby banner, not at creation. Free leagues: no extra copy. */}
        {buyIn > 0 && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Info className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <p className="text-[10px] text-amber-400/90">
              Your seat is reserved. Pay your entry fee from the league lobby before the draft can start.
            </p>
          </div>
        )}

        <Button
          className="w-full bg-primary hover:bg-primary/90"
          onClick={() => onComplete?.(createdLeague.id)}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Go to League
        </Button>
      </div>
    );
  }

  // ── Wizard ──
  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-display font-bold text-foreground">Create NFL League</h1>
          <span className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</span>
        </div>
        <div className="flex gap-1">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                i <= step ? 'bg-primary' : 'bg-muted/50'
              )}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{STEPS[step]}</p>
      </div>

      {/* Step content */}
      {step === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-5">
            {/* League name */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground">League Name</label>
              <Input
                placeholder="e.g. Trash Bowl 2026"
                value={leagueName}
                onChange={(e) => setLeagueName(e.target.value)}
                maxLength={40}
                className="bg-muted/30"
              />
              <p className="text-[10px] text-muted-foreground text-right">{leagueName.length}/40</p>
            </div>

            {/* Team count */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground">Number of Teams</label>
              <div className="grid grid-cols-4 gap-2">
                {[8, 10, 12, 14].map(n => (
                  <button
                    key={n}
                    onClick={() => setTeamCount(n)}
                    className={cn(
                      'py-3 rounded-lg text-sm font-bold transition-all border',
                      teamCount === n
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* SAB-505: Scoring mode */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground">Scoring</label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['sabotage', 'Sabotage', 'Inverted, worst players win'],
                  ['standard', 'Standard', 'Conventional, no PPR'],
                  ['half_ppr', 'Half-PPR', '0.5 pts per reception'],
                  ['ppr', 'PPR', '1 pt per reception'],
                ] as const).map(([type, title, desc]) => (
                  <button
                    key={type}
                    onClick={() => selectScoringType(type)}
                    className={cn(
                      'p-3 rounded-lg text-left border transition-all',
                      scoringType === type
                        ? 'bg-primary/10 border-primary/30'
                        : 'bg-muted/30 border-border hover:border-primary/20'
                    )}
                  >
                    <p className={cn('text-sm font-bold', scoringType === type ? 'text-primary' : 'text-foreground')}>
                      {title}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{desc}</p>
                  </button>
                ))}
              </div>
              {isConventional && (
                <p className="text-[10px] text-muted-foreground">
                  Conventional leagues use a QB/RB/RB/WR/WR/TE/FLEX/K/P roster this season (no DST/IDP yet).
                </p>
              )}
            </div>

            {/* Season */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground">Season</label>
              <div className="py-3 px-4 rounded-lg bg-muted/30 border border-border text-sm text-foreground font-medium">
                2026 NFL Season
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-1">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-semibold text-foreground">Roster Configuration</p>
                <p className="text-[10px] text-muted-foreground">{starterSlots} starters · {roster.BN} bench · {roster.IR} IR = {totalSlots} total</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRoster(isConventional
                  ? { ...DEFAULT_ROSTER, DST: 0, COACH: 0, IDP: 0 }
                  : { ...DEFAULT_ROSTER })}
                className="text-[10px] h-7 text-primary hover:text-primary/80"
              >
                Reset Default
              </Button>
            </div>

            {/* Starters */}
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold pt-1 pb-1">Starters</p>
            {(isConventional
              ? ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'P']
              : ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K', 'P', 'DST', 'COACH', 'IDP']
            ).map(pos => (
              <RosterSlotRow
                key={pos}
                position={pos}
                count={roster[pos as keyof RosterConfig]}
                info={POSITION_INFO[pos]}
                onDecrement={() => updateRoster(pos, -1)}
                onIncrement={() => updateRoster(pos, 1)}
                min={POSITION_LIMITS[pos][0]}
                max={POSITION_LIMITS[pos][1]}
              />
            ))}

            {/* Bench / IR */}
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold pt-3 pb-1">Reserve</p>
            {['BN', 'IR'].map(pos => (
              <RosterSlotRow
                key={pos}
                position={pos}
                count={roster[pos as keyof RosterConfig]}
                info={POSITION_INFO[pos]}
                onDecrement={() => updateRoster(pos, -1)}
                onIncrement={() => updateRoster(pos, 1)}
                min={POSITION_LIMITS[pos][0]}
                max={POSITION_LIMITS[pos][1]}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-5">
            {/* Draft type */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground">Draft Type</label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['snake', 'Snake (Live)', 'Real-time draft with pick timer'],
                  ['slow', 'Slow Draft', '4-12 hours per pick, async'],
                ] as const).map(([type, title, desc]) => (
                  <button
                    key={type}
                    onClick={() => {
                      setDraftType(type);
                      setDraftTimer(type === 'snake' ? 90 : 28800);
                    }}
                    className={cn(
                      'p-3 rounded-lg text-left border transition-all',
                      draftType === type
                        ? 'bg-primary/10 border-primary/30'
                        : 'bg-muted/30 border-border hover:border-primary/20'
                    )}
                  >
                    <p className={cn('text-sm font-bold', draftType === type ? 'text-primary' : 'text-foreground')}>
                      {title}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Pick timer */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                Pick Timer
              </label>
              {draftType === 'snake' ? (
                <div className="grid grid-cols-3 gap-2">
                  {[60, 90, 120].map(s => (
                    <button
                      key={s}
                      onClick={() => setDraftTimer(s)}
                      className={cn(
                        'py-2.5 rounded-lg text-sm font-bold border transition-all',
                        draftTimer === s
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                      )}
                    >
                      {s}s
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    [14400, '4 hrs'],
                    [28800, '8 hrs'],
                    [43200, '12 hrs'],
                  ].map(([s, label]) => (
                    <button
                      key={String(s)}
                      onClick={() => setDraftTimer(s as number)}
                      className={cn(
                        'py-2.5 rounded-lg text-sm font-bold border transition-all',
                        draftTimer === s
                          ? 'bg-primary/10 text-primary border-primary/30'
                          : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* SAB-534 Chunk 2: overnight pause (slow drafts only) */}
            {draftType === 'slow' && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  Overnight Pause
                </label>
                <button
                  onClick={() => setPauseOvernight(v => !v)}
                  className={cn(
                    'w-full py-2.5 px-3 rounded-lg text-sm font-semibold border transition-all text-left flex items-center justify-between',
                    pauseOvernight
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                  )}
                >
                  <span>{pauseOvernight ? 'On: clocks stop overnight' : 'Off: clocks run overnight'}</span>
                  <span className="text-[10px]">{pauseOvernight ? 'ON' : 'OFF'}</span>
                </button>
                {pauseOvernight && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">Pause from (ET)</p>
                      <select
                        value={pauseStartHour}
                        onChange={e => setPauseStartHour(Number(e.target.value))}
                        className="w-full py-2 px-2 rounded-lg text-sm bg-muted/30 border border-border text-foreground"
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">Until (ET)</p>
                      <select
                        value={pauseEndHour}
                        onChange={e => setPauseEndHour(Number(e.target.value))}
                        className="w-full py-2 px-2 rounded-lg text-sm bg-muted/30 border border-border text-foreground"
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Draft date */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                Draft Date (Optional)
              </label>
              <Input
                type="datetime-local"
                value={draftDate}
                onChange={(e) => setDraftDate(e.target.value)}
                className="bg-muted/30"
              />
              <p className="text-[10px] text-muted-foreground">
                You can set or change the draft date later in league settings
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {/* Buy-in */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                Buy-In Amount
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[0, 10, 25, 50, 100].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setBuyIn(amount)}
                    className={cn(
                      'py-2.5 rounded-lg text-sm font-bold border transition-all',
                      buyIn === amount
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                    )}
                  >
                    {amount === 0 ? 'Free' : `$${amount}`}
                  </button>
                ))}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    placeholder="Custom"
                    value={buyIn > 0 && ![0, 10, 25, 50, 100].includes(buyIn) ? buyIn : ''}
                    onChange={(e) => setBuyIn(Number(e.target.value) || 0)}
                    className="pl-7 h-full text-sm font-bold bg-muted/30"
                    min={0}
                    max={10000}
                  />
                </div>
              </div>
              {buyIn > 0 && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                  <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <p className="text-[10px] text-muted-foreground">
                    Payment is collected from each player at the moment they join. Full prize pool when filled: ${buyIn * teamCount}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SAB-425: visibility toggle */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <label className="text-xs font-semibold text-foreground">
                League Visibility
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(['private', 'public'] as const).map(v => (
                  <button
                    key={v}
                    onClick={() => setVisibility(v)}
                    className={cn(
                      'py-3 rounded-lg text-sm font-bold border transition-all flex flex-col items-center gap-0.5',
                      visibility === v
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                    )}
                  >
                    <span className="capitalize">{v}</span>
                    <span className="text-[9px] font-normal opacity-80">
                      {v === 'private' ? 'Invite code only' : 'Anyone can join'}
                    </span>
                  </button>
                ))}
              </div>
              {visibility === 'public' && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
                  <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <p className="text-[10px] text-muted-foreground">
                    Listed on the Browse page. Once it fills, it drops off automatically. You can flip it back to private from League Settings any time.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Review summary */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <p className="text-xs font-bold text-foreground uppercase tracking-wider">Review</p>
              <div className="space-y-2">
                <ReviewRow label="League Name" value={leagueName.trim()} />
                <ReviewRow label="Teams" value={String(teamCount)} />
                <ReviewRow label="Season" value="2026" />
                <ReviewRow label="Roster Size" value={`${totalSlots} (${starterSlots} starters)`} />
                <ReviewRow label="Draft Type" value={draftType === 'snake' ? 'Snake (Live)' : 'Slow Draft'} />
                <ReviewRow
                  label="Pick Timer"
                  value={draftType === 'snake'
                    ? `${draftTimer} seconds`
                    : `${draftTimer / 3600} hours`
                  }
                />
                {draftDate && <ReviewRow label="Draft Date" value={formatDraftTime(localInputToInstant(draftDate))} />}
                <ReviewRow label="Buy-In" value={buyIn === 0 ? 'Free' : `$${buyIn}`} />
                {buyIn > 0 && <ReviewRow label="Prize Pool" value={`$${buyIn * teamCount}`} highlight />}
                <ReviewRow label="Visibility" value={visibility === 'public' ? 'Public (browse)' : 'Private (invite only)'} />
                <ReviewRow label="Scoring" value={
                  scoringType === 'sabotage' ? 'Sabotage (Trash Points)'
                  : scoringType === 'standard' ? 'Standard'
                  : scoringType === 'half_ppr' ? 'Half-PPR'
                  : 'PPR'
                } />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-2">
        {step > 0 ? (
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setStep(s => s - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        ) : onClose ? (
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
        ) : null}

        {step < STEPS.length - 1 ? (
          <Button
            className="flex-1 bg-primary hover:bg-primary/90"
            onClick={() => setStep(s => s + 1)}
            disabled={!canProceed}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            className="flex-1 bg-primary hover:bg-primary/90"
            onClick={handleCreate}
            disabled={isSubmitting || !canProceed}
          >
            {isSubmitting ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Trophy className="h-4 w-4 mr-2" />
            )}
            Create League
          </Button>
        )}
      </div>
    </div>
  );
}

function RosterSlotRow({
  position, count, info, onDecrement, onIncrement, min, max,
}: {
  position: string; count: number; info: string;
  onDecrement: () => void; onIncrement: () => void;
  min: number; max: number;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-bold border-border text-foreground shrink-0 w-12 justify-center">
          {position}
        </Badge>
        <span className="text-[10px] text-muted-foreground truncate">{info}</span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={onDecrement}
          disabled={count <= min}
          className={cn(
            'h-7 w-7 rounded-md border flex items-center justify-center transition-colors',
            count <= min
              ? 'border-border/30 text-muted-foreground/30'
              : 'border-border text-foreground hover:bg-muted/50'
          )}
        >
          <Minus className="h-3 w-3" />
        </button>
        <span className="text-sm font-bold text-foreground w-5 text-center tabular-nums">{count}</span>
        <button
          onClick={onIncrement}
          disabled={count >= max}
          className={cn(
            'h-7 w-7 rounded-md border flex items-center justify-center transition-colors',
            count >= max
              ? 'border-border/30 text-muted-foreground/30'
              : 'border-border text-foreground hover:bg-muted/50'
          )}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('text-xs font-semibold', highlight ? 'text-primary' : 'text-foreground')}>{value}</span>
    </div>
  );
}
